import { useEffect, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { MessageCircle, BookOpen, Users, HeartHandshake, ShieldCheck, Eye, Menu, X, Search, Filter, CheckCircle, Trash2, AlertTriangle, Lock, LogOut, Edit } from "lucide-react";
import { supabase } from "./lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente para scroll al top en cada cambio de ruta
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Context para gestión global de historias (ahora con Supabase)
const HistoriasContext = createContext();

// Context para autenticación admin
const AuthContext = createContext();

// Context para gestión de países/regiones
const CountryContext = createContext();

// Configuración de países disponibles
const COUNTRIES_CONFIG = {
  default: {
    code: 'default',
    name: 'Global',
    lang: 'es',
    basePath: ''
  },
  es: {
    code: 'es',
    name: 'España',
    lang: 'es',
    basePath: '/es'
  },
  ar: {
    code: 'ar',
    name: 'Argentina',
    lang: 'es',
    basePath: '/ar'
  },
  mx: {
    code: 'mx',
    name: 'México',
    lang: 'es',
    basePath: '/mx'
  }
};

// Hook para usar el contexto de país
const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    return { country: COUNTRIES_CONFIG.default, setCountry: () => {} };
  }
  return context;
};

// Provider del contexto de país
const CountryProvider = ({ children, countryCode = 'default' }) => {
  const [country, setCountry] = useState(COUNTRIES_CONFIG[countryCode] || COUNTRIES_CONFIG.default);
  
  return (
    <CountryContext.Provider value={{ country, setCountry, COUNTRIES_CONFIG }}>
      {children}
    </CountryContext.Provider>
  );
};

// Layout para rutas de país - usa Outlet para renderizar rutas hijas
const CountryLayout = ({ countryCode }) => {
  return (
    <CountryProvider countryCode={countryCode}>
      <Outlet />
    </CountryProvider>
  );
};

// Hook personalizado para usar el contexto de historias
const useHistorias = () => {
  const context = useContext(HistoriasContext);
  if (!context) {
    throw new Error('useHistorias debe ser usado dentro de HistoriasProvider');
  }
  return context;
};

// Hook para autenticación
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

// Función helper para formatear fecha relativa
const formatRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Hoy";
  if (diffDays === 1) return "Hace 1 día";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffDays < 14) return "Hace 1 semana";
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
  return `Hace ${Math.floor(diffDays / 30)} mes${Math.floor(diffDays / 30) > 1 ? 'es' : ''}`;
};

// Auth Provider para Supabase Auth
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Verificar sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// Provider del contexto de historias (con Supabase)
const HistoriasProvider = ({ children }) => {
  const [historias, setHistorias] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar historias desde Supabase
  const cargarHistorias = useCallback(async (isAdmin = false) => {
    try {
      setLoading(true);
      let query = supabase
        .from('ecos_stories')
        .select('*')
        .order('created_at', { ascending: false });
      
      // Si no es admin, RLS solo devolverá las aprobadas
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transformar datos de Supabase al formato esperado por la app
      const historiasFormateadas = (data || []).map(story => ({
        id: story.id,
        tipo: story.type,
        emoji: story.emoji,
        titulo: story.title || null,
        contenido: story.content,
        fecha: formatRelativeDate(story.public_date || story.created_at),
        timestamp: new Date(story.created_at).getTime(),
        color: story.color,
        bgColor: story.bg_color,
        status: story.status === 'approved' ? 'aprobada' : 
                story.status === 'pending' ? 'pendiente' : 
                story.status === 'rejected' ? 'eliminada' : 
                story.status === 'inappropriate' ? 'inapropiada' : story.status,
        moderation_note: story.moderation_note,
        codigo_de_pais: story.codigo_de_pais || 'es'
      }));
      
      setHistorias(historiasFormateadas);
    } catch (error) {
      console.error('Error al cargar historias:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar al montar
  useEffect(() => {
    cargarHistorias();
  }, [cargarHistorias]);

  // Función para añadir una nueva historia (INSERT en Supabase)
  const agregarHistoria = async (nuevaHistoria) => {
    // Validar contenido
    const content = (nuevaHistoria.contenido || '').trim();
    if (!content) {
      return { success: false, error: 'No content provided' };
    }

    // Construir datos para insertar
    const storyData = {
      type: nuevaHistoria.tipo || 'Otro',
      emoji: nuevaHistoria.emoji || '💭',
      content: content,
      color: nuevaHistoria.color || '#7B4EFF',
      bg_color: nuevaHistoria.bgColor || '#F3F0FF',
      codigo_de_pais: nuevaHistoria.codigoPais || 'es'
    };

    // Añadir título si existe
    if (nuevaHistoria.titulo && nuevaHistoria.titulo.trim()) {
      storyData.title = nuevaHistoria.titulo.trim();
    }

    // Insertar en Supabase
    const { error } = await supabase
      .from('ecos_stories')
      .insert([storyData]);
    
    if (error) {
      console.error('Error inserting story:', error.message);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  };

  // Función para actualizar el status de una historia (solo admin autenticado)
  const actualizarStatusHistoria = async (id, nuevoStatus, note = null) => {
    try {
      // Mapear status de español a inglés para Supabase
      const statusMap = {
        'aprobada': 'approved',
        'pendiente': 'pending',
        'eliminada': 'rejected',
        'inapropiada': 'inappropriate'
      };
      
      const updateData = {
        status: statusMap[nuevoStatus] || nuevoStatus,
        ...(nuevoStatus === 'aprobada' && { public_date: new Date().toISOString() }),
        ...(note && { moderation_note: note })
      };
      
      const { error } = await supabase
        .from('ecos_stories')
        .update(updateData)
        .eq('id', id);
      
      if (error) throw error;
      
      // Actualizar estado local
      setHistorias(prev => prev.map(historia => 
        historia.id === id ? { ...historia, status: nuevoStatus } : historia
      ));
      
      return { success: true };
    } catch (error) {
      console.error('Error al actualizar historia:', error);
      return { success: false, error };
    }
  };

  return (
    <HistoriasContext.Provider value={{ historias, loading, agregarHistoria, actualizarStatusHistoria, cargarHistorias }}>
      {children}
    </HistoriasContext.Provider>
  );
};

// Componente de Navegación Superior - Dos niveles con menú móvil
const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header style={{ 
      background: '#f2f8f7', 
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      {/* Nivel 1: Logo + Botón Salir rápido + Hamburguesa móvil */}
      <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10" style={{ paddingTop: '16px', paddingBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <HeartHandshake size={30} strokeWidth={1.5} style={{ color: '#1E293B', opacity: 0.7 }} />
            <h1 style={{ 
              fontSize: '26px', 
              fontWeight: 600, 
              color: '#1E293B',
              fontFamily: 'Poppins, Inter, system-ui, sans-serif',
              margin: 0
            }}>
              Ecos del Silencio
            </h1>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => { window.location.replace('https://www.google.com'); }}
              style={{
                background: 'rgba(148, 163, 184, 0.08)',
                border: '1px solid #E2E8F0',
                borderRadius: '20px',
                color: '#94A3B8',
                fontSize: '13px',
                fontFamily: 'Inter, system-ui, sans-serif',
                cursor: 'pointer',
                padding: '8px 16px'
              }}
            >
              Salir rápido
            </button>

            {/* Hamburguesa solo en móvil */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-menu-btn"
              style={{
                display: 'none',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                color: '#64748B'
              }}
              aria-label="Menú de navegación"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </div>

      {/* Nivel 2: Barra de navegación (escritorio) */}
      <nav className="desktop-nav" style={{ borderTop: '1px solid #F1F5F9', background: '#FAFBFC' }}>
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Link to="/" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Inicio</Link>
            <Link to="/muro" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Leer historias</Link>
            <Link to="/participa" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Participa</Link>
            <Link to="/aprende" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Aprende</Link>
            <Link to="/comics" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Cómics</Link>
            <Link to="/compromisos" className="nav-pill" style={{ textDecoration: 'none', fontSize: '14px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '8px 18px', borderRadius: '20px', background: 'rgba(100, 116, 139, 0.06)', border: '1px solid transparent', transition: 'background 0.2s, border-color 0.2s' }}>Compromisos</Link>
          </div>
        </div>
      </nav>

      {/* Menú móvil desplegable */}
      <nav 
        className="mobile-nav"
        style={{ 
          maxHeight: mobileMenuOpen ? '400px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease-in-out',
          background: '#FAFBFC',
          borderTop: '1px solid #F1F5F9'
        }}
      >
        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Link to="/" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Inicio</Link>
          <Link to="/muro" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Leer historias</Link>
          <Link to="/participa" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Participa</Link>
          <Link to="/aprende" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Aprende</Link>
          <Link to="/comics" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Cómics</Link>
          <Link to="/compromisos" onClick={closeMobileMenu} style={{ textDecoration: 'none', fontSize: '16px', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', padding: '12px 16px', borderRadius: '12px', background: 'rgba(100, 116, 139, 0.06)' }}>Compromisos</Link>
        </div>
      </nav>
    </header>
  );
};

const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  // Función para hacer scroll suave a la sección principal
  const scrollToMainContent = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="homepage" style={{ background: '#E7FFF8' }}>
      
      {/* Subtítulo bajo el menú */}
      <div style={{ 
        textAlign: 'center', 
        padding: '24px 20px 0',
        background: '#E7FFF8'
      }}>
        <p style={{ 
          color: '#00C3A8', 
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
          fontWeight: 600,
          letterSpacing: '0.5px',
          margin: 0
        }}>
          Historias y voces contra el bullying
        </p>
        {/* Visual separator - two subtle centered lines */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          marginTop: '12px'
        }}>
          <div style={{ 
            width: '200px',
            height: '1.5px', 
            background: '#00C3A8',
            opacity: 0.5,
            borderRadius: '1px'
          }} />
          <div style={{ 
            width: '120px',
            height: '1px', 
            background: '#00C3A8',
            opacity: 0.3,
            borderRadius: '1px'
          }} />
        </div>
      </div>

      {/* ========== HERO PRINCIPAL - DISEÑO ESTRUCTURADO ========== */}
      <div 
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          background: '#E7FFF8',
          padding: '0 40px 40px',
          marginTop: '-10px',
          gap: '60px'
        }}
        className="hero-container"
      >
        {/* Columna de Imagen */}
        <div 
          className="hero-image-col"
          style={{
            flex: '0 0 45%',
            maxWidth: '520px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}
        >
          <div 
            style={{
              width: '100%',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}
          >
            <img 
              src="/hero-image.png" 
              alt="Jóvenes conversando - Ecos del Silencio"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                marginBottom: '-45%'
              }}
            />
          </div>
        </div>

        {/* Columna de Texto */}
        <div 
          className="hero-text-col"
          style={{
            flex: '0 0 50%',
            maxWidth: '580px',
            textAlign: 'left'
          }}
        >
          {/* Título H1 */}
          <h1 
            style={{
              fontSize: 'clamp(1.75rem, 4vw, 3rem)',
              fontWeight: 700,
              color: '#1E293B',
              fontFamily: 'Poppins, system-ui, sans-serif',
              lineHeight: 1.2,
              marginBottom: '20px'
            }}
          >
            No estás exagerando.<br/>
            Lo que sientes importa.
          </h1>

          {/* Subtítulo */}
          <p 
            style={{
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: '#475569',
              fontFamily: 'Inter, system-ui, sans-serif',
              lineHeight: 1.7,
              marginBottom: '32px',
              fontWeight: 400
            }}
          >
            Aquí puedes leer, entender y hablar sin miedo.<br/>
            Sin nombres. Sin juicios. A tu ritmo.
          </p>

          {/* Botones CTA */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '14px' }}>
            {/* CTA Principal */}
            <a
              href="/aprende"
              data-testid="hero-empezar-btn"
              style={{
                padding: '16px 40px',
                background: '#00C3A8',
                color: 'white',
                fontSize: '17px',
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 600,
                border: 'none',
                borderRadius: '50px',
                cursor: 'pointer',
                textDecoration: 'none',
                boxShadow: '0 4px 20px rgba(0, 195, 168, 0.35)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              Empezar aquí
            </a>

            {/* CTA Secundario */}
            <a
              href="/muro"
              style={{
                padding: '14px 32px',
                background: 'transparent',
                color: '#475569',
                fontSize: '15px',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 500,
                border: '2px solid #64748B',
                borderRadius: '50px',
                cursor: 'pointer',
                textDecoration: 'none',
                transition: 'border-color 0.2s, color 0.2s'
              }}
            >
              Leer historias reales
            </a>
          </div>

          {/* Microcopy */}
          <p 
            style={{
              marginTop: '20px',
              fontSize: '13px',
              color: '#475569',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 400,
              fontStyle: 'italic'
            }}
          >
            Nadie verá quién eres. Puedes irte cuando quieras.
          </p>

          {/* Enlace secundario */}
          <a
            href="/presentacion"
            data-testid="conoce-proyecto-btn"
            style={{
              display: 'inline-block',
              marginTop: '24px',
              color: '#64748B',
              fontSize: '14px',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 400,
              textDecoration: 'none',
              borderBottom: '1px solid #CBD5E1',
              paddingBottom: '2px',
              transition: 'color 0.2s'
            }}
          >
            Conoce el proyecto
          </a>
        </div>
      </div>

      {/* CSS para responsive */}
      <style>{`
        @media (max-width: 900px) {
          .hero-container {
            flex-direction: column !important;
            padding: 100px 24px 40px !important;
            gap: 32px !important;
            min-height: auto !important;
          }
          .hero-image-col {
            flex: none !important;
            max-width: 100% !important;
            width: 100% !important;
            order: 1;
          }
          .hero-image-col > div {
            max-width: 400px;
            margin: 0 auto;
          }
          .hero-text-col {
            flex: none !important;
            max-width: 100% !important;
            width: 100% !important;
            text-align: center !important;
            order: 2;
          }
          .hero-text-col > div {
            align-items: center !important;
          }
        }
      `}</style>

      {/* ========== SECCIÓN: ELIGE CÓMO EMPEZAR ========== */}
      <div 
        style={{
          width: '100%',
          background: '#F8FDFB',
          padding: '80px 24px',
          borderTop: '1px solid rgba(0, 195, 168, 0.1)'
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          {/* Título */}
          <h2 
            style={{
              fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
              fontWeight: 700,
              color: '#1E293B',
              fontFamily: 'Poppins, system-ui, sans-serif',
              marginBottom: '48px'
            }}
          >
            Elige cómo empezar. A tu ritmo.
          </h2>

          {/* Fila 1: 3 tarjetas */}
          <div 
            className="cards-row-1"
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '24px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '24px'
            }}
          >
            {/* Tarjeta 1: Leer historias */}
            <div 
              style={{
                flex: '1 1 280px',
                maxWidth: '320px',
                background: 'white',
                borderRadius: '20px',
                padding: '28px 24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                textAlign: 'left',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="card-elige"
            >
              <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>📖</div>
              <h3 
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  marginBottom: '8px'
                }}
              >
                Leer historias
              </h3>
              <p 
                style={{
                  fontSize: '0.9rem',
                  color: '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 1.6,
                  marginBottom: '18px'
                }}
              >
                Historias reales de personas como tú.<br/>A veces leer también ayuda.
              </p>
              <a 
                href="/muro"
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  background: '#00C3A8',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 600,
                  borderRadius: '30px',
                  textDecoration: 'none'
                }}
              >
                Leer historias
              </a>
            </div>

            {/* Tarjeta 2: Contar lo tuyo */}
            <div 
              style={{
                flex: '1 1 280px',
                maxWidth: '320px',
                background: 'white',
                borderRadius: '20px',
                padding: '28px 24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                textAlign: 'left',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="card-elige"
            >
              <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>✍️</div>
              <h3 
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  marginBottom: '8px'
                }}
              >
                Contar lo tuyo
              </h3>
              <p 
                style={{
                  fontSize: '0.9rem',
                  color: '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 1.6,
                  marginBottom: '18px'
                }}
              >
                Si quieres, puedes compartir tu experiencia. Es anónimo y sin presión.
              </p>
              <a 
                href="/participa"
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  background: '#00C3A8',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 600,
                  borderRadius: '30px',
                  textDecoration: 'none'
                }}
              >
                Contar mi historia
              </a>
            </div>

            {/* Tarjeta 3: Entender mejor */}
            <div 
              style={{
                flex: '1 1 280px',
                maxWidth: '320px',
                background: 'white',
                borderRadius: '20px',
                padding: '28px 24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                textAlign: 'left',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="card-elige"
            >
              <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>💡</div>
              <h3 
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  marginBottom: '8px'
                }}
              >
                Entender mejor
              </h3>
              <p 
                style={{
                  fontSize: '0.9rem',
                  color: '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 1.6,
                  marginBottom: '18px'
                }}
              >
                Ideas claras para entender qué pasa y qué puedes hacer.
              </p>
              <a 
                href="/aprende"
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  background: '#F59E0B',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 600,
                  borderRadius: '30px',
                  textDecoration: 'none'
                }}
              >
                Aprender
              </a>
            </div>
          </div>

          {/* Fila 2: 2 tarjetas centradas */}
          <div 
            className="cards-row-2"
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '24px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            {/* Tarjeta 4: Dar un paso */}
            <div 
              style={{
                flex: '1 1 280px',
                maxWidth: '320px',
                background: 'white',
                borderRadius: '20px',
                padding: '28px 24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                textAlign: 'left',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="card-elige"
            >
              <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>👏</div>
              <h3 
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  marginBottom: '8px'
                }}
              >
                Dar un paso
              </h3>
              <p 
                style={{
                  fontSize: '0.9rem',
                  color: '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 1.6,
                  marginBottom: '18px'
                }}
              >
                Pequeñas acciones que ayudan a cambiar las cosas.
              </p>
              <a 
                href="/compromisos"
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  background: '#00C3A8',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 600,
                  borderRadius: '30px',
                  textDecoration: 'none'
                }}
              >
                Ver compromisos
              </a>
            </div>

            {/* Tarjeta 5: Ver historias visuales */}
            <div 
              style={{
                flex: '1 1 280px',
                maxWidth: '320px',
                background: 'white',
                borderRadius: '20px',
                padding: '28px 24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                textAlign: 'left',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              className="card-elige"
            >
              <div style={{ fontSize: '2.2rem', marginBottom: '14px' }}>📚</div>
              <h3 
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: '#1E293B',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  marginBottom: '8px'
                }}
              >
                Ver historias visuales
              </h3>
              <p 
                style={{
                  fontSize: '0.9rem',
                  color: '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  lineHeight: 1.6,
                  marginBottom: '18px'
                }}
              >
                A veces una imagen explica lo que cuesta decir.
              </p>
              <a 
                href="/comics"
                style={{
                  display: 'inline-block',
                  padding: '10px 22px',
                  background: '#F59E0B',
                  color: 'white',
                  fontSize: '14px',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 600,
                  borderRadius: '30px',
                  textDecoration: 'none'
                }}
              >
                Ver cómics
              </a>
            </div>
          </div>
        </div>

        {/* CSS para cards responsive */}
        <style>{`
          .card-elige:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
          }
          @media (max-width: 768px) {
            .cards-row-1, .cards-row-2 {
              flex-direction: column !important;
              align-items: center !important;
            }
            .card-elige {
              width: 100% !important;
              max-width: 100% !important;
            }
          }
        `}</style>
      </div>

      {/* ========== SECCIÓN DE CONTENIDO ========== */}
      <div id="contenido" className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 relative z-10">
        
        {/* Elementos decorativos mínimos */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '100px', height: '100px', borderRadius: '50%', background: '#7B4EFF', opacity: 0.05 }}></div>
        <div style={{ position: 'absolute', bottom: '20%', left: '8%', width: '80px', height: '80px', borderRadius: '50%', background: '#00C3A8', opacity: 0.04 }}></div>

        {/* ========== BLOQUE DE HUMANIDAD ========== */}
        <div 
          className="max-w-3xl mx-auto mb-16 rounded-2xl p-8 text-center"
          style={{ 
            background: 'rgba(123, 78, 255, 0.05)',
            border: '2px solid rgba(123, 78, 255, 0.1)'
          }}
        >
          <div className="flex justify-center mb-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ 
                background: 'rgba(123, 78, 255, 0.1)'
              }}
            >
              <span style={{ fontSize: '32px' }}>🤲</span>
            </div>
          </div>
          
          <h3 
            className="text-2xl font-bold mb-4"
            style={{ 
              color: '#1E293B',
              fontFamily: 'Poppins, system-ui, sans-serif'
            }}
          >
            No tienes que hacerlo perfecto.
          </h3>
          
          <p 
            className="text-base leading-relaxed"
            style={{ 
              color: '#64748B',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}
          >
            Aquí no se juzga cómo escribes ni cuánto cuentas.<br/>
            A veces solo poner una palabra ya es un comienzo.
          </p>
        </div>

      </div>
    </div>
  );
};

// Componente de Layout Base con nueva paleta de marca
const BaseLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#E7FFF8' }}>
      {/* Contenido principal */}
      <div className="flex-grow relative overflow-hidden">
        {/* Elementos decorativos mínimos - 20% del original */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: '100px', height: '100px', borderRadius: '50%', background: '#7B4EFF', opacity: 0.05 }}></div>
        <div style={{ position: 'absolute', bottom: '20%', left: '8%', width: '80px', height: '80px', borderRadius: '50%', background: '#00C3A8', opacity: 0.04 }}></div>
        
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16 relative z-10">
          {children}
        </div>
      </div>

      {/* Footer minimalista */}
      <footer style={{ background: '#F8FAFA', borderTop: '1px solid #E8EDED', paddingTop: '2.5rem', paddingBottom: '2rem' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          {/* Título y descripción */}
          <p style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Ecos del Silencio
          </p>
          <p style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.8rem', marginBottom: '1.5rem', maxWidth: '320px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            Un espacio para compartir experiencias de bullying, exclusión y presión social.
          </p>

          {/* Enlaces principales */}
          <div className="footer-main-links" style={{ marginBottom: '1.25rem', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem 1.5rem' }}>
            <Link to="/historias" style={{ color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Leer historias
            </Link>
            <Link to="/participa" style={{ color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Escribe tu experiencia
            </Link>
            <Link to="/aprende" style={{ color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
              Aprende
            </Link>
          </div>

          {/* CSS para footer en móvil */}
          <style>{`
            @media (max-width: 480px) {
              .footer-main-links {
                flex-direction: column !important;
                align-items: center !important;
                gap: 0.75rem !important;
              }
            }
          `}</style>

          {/* Enlaces legales */}
          <div style={{ marginBottom: '1rem' }}>
            <Link to="/privacidad" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.75rem', textDecoration: 'none', margin: '0 0.5rem' }}>
              Política de privacidad
            </Link>
            <Link to="/aviso-legal" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.75rem', textDecoration: 'none', margin: '0 0.5rem' }}>
              Aviso legal
            </Link>
          </div>

          {/* Enlace institucional discreto */}
          <div>
            <Link to="/presentacion" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.7rem', textDecoration: 'none' }}>
              Información institucional
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Participa = () => {
  const { agregarHistoria } = useHistorias();
  const [contenido, setContenido] = useState('');
  const [rol, setRol] = useState('');
  const [titulo, setTitulo] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Mapeo de roles a configuración visual
  const rolesConfig = {
    "Lo estoy viviendo": { tipo: "Víctima", emoji: "😔", color: "#7B4EFF", bgColor: "#F3F0FF" },
    "Soy testigo": { tipo: "Testigo", emoji: "👀", color: "#00C3A8", bgColor: "#E6FAF5" },
    "Soy familiar": { tipo: "Familiar", emoji: "👨‍👩‍👧", color: "#FF5F91", bgColor: "#FFF0F5" },
    "Soy docente": { tipo: "Docente", emoji: "👩‍🏫", color: "#7B4EFF", bgColor: "#F3F0FF" },
    "Otro": { tipo: "Otro", emoji: "💭", color: "#00C3A8", bgColor: "#E6FAF5" }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const textoHistoria = contenido?.trim() || '';
    
    if (!textoHistoria) {
      setMensaje('Por favor, escribe tu historia');
      return;
    }

    setEnviando(true);
    setMensaje('');

    try {
      // Detectar país desde la URL actual
      const path = window.location.pathname;
      let codigoPais = 'es';
      if (path.startsWith('/ar')) codigoPais = 'ar';
      else if (path.startsWith('/mx')) codigoPais = 'mx';
      
      // Rol es opcional - usar "Otro" si no se selecciona o es inválido
      const selectedRol = (rol && rol !== '' && rol !== 'Selecciona tu rol (opcional)' && rolesConfig[rol]) 
        ? rol 
        : 'Otro';
      const config = rolesConfig[selectedRol] || rolesConfig['Otro'];
      
      const nuevaHistoria = {
        tipo: config.tipo,
        emoji: config.emoji,
        titulo: titulo?.trim() || null,
        contenido: textoHistoria,
        color: config.color,
        bgColor: config.bgColor,
        codigoPais: codigoPais
      };

      const result = await agregarHistoria(nuevaHistoria);
      
      if (result.success) {
        setContenido('');
        setRol('');
        setTitulo('');
        setMensaje('✓ ¡Tu historia ha sido enviada! Aparecerá en el Muro después de ser moderada.');
      } else {
        console.error('Submit error:', result.error);
        setMensaje('❌ Hubo un error al enviar tu historia. Por favor, inténtalo de nuevo.');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setMensaje('❌ Hubo un error al enviar tu historia. Por favor, inténtalo de nuevo.');
    } finally {
      setEnviando(false);
      setTimeout(() => setMensaje(''), 5000);
    }
  };

  return (
    <BaseLayout>
      {/* Título principal */}
      <div className="rounded-3xl p-8 sm:p-12 lg:p-16 mb-10 text-center" style={{ background: 'white', boxShadow: '0 2px 12px rgba(123, 78, 255, 0.08)' }}>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', lineHeight: 1.1 }}>
          Comparte tu experiencia
        </h1>
        <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto mb-4" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Este espacio es para quien lo vive, para quien acompaña y también para quien quiere reparar. Cada mirada importa.
        </p>
        <p className="text-base leading-relaxed max-w-3xl mx-auto" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Si quieres contar algo, este es tu espacio.<br/>
          Es anónimo, sin presión y sin juicios.
        </p>
        
        {/* Frase de cierre */}
        <div className="mt-6 text-center">
          <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.8 }}>
            Pedir ayuda es un paso importante para que las cosas cambien.<br/>
            <span style={{ color: '#7B4EFF' }}>Mereces sentirte bien.</span>
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Formulario para compartir historia */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 mb-8" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '4px solid #7B4EFF' }}>
          
          {/* Microtexto encima del textarea */}
          <p className="text-base mb-4 text-center" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic' }}>
            Escribe como puedas.<br/>
            No tiene que estar bien escrito.
          </p>
          
          {/* Campo de título (opcional) */}
          <input
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Título (opcional)"
            className="w-full p-3 border rounded-xl text-base mb-3"
            style={{ borderColor: '#E2E8F0', color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}
            maxLength={100}
          />
          
          {/* Campo de texto */}
          <textarea 
            value={contenido}
            onChange={(e) => setContenido(e.target.value)}
            placeholder="Escribe aquí, a tu ritmo."
            rows="8"
            className="w-full p-4 border rounded-xl text-base mb-4"
            style={{ borderColor: '#E2E8F0', color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}
          />
          
          {/* Selector de rol y botón */}
          <div className="flex flex-col gap-4 mb-4">
            <select 
              value={rol}
              onChange={(e) => setRol(e.target.value)}
              className="w-full p-3 border rounded-xl text-base" 
              style={{ borderColor: '#E2E8F0', fontFamily: 'Inter, system-ui, sans-serif', color: '#64748B' }}
            >
              <option>Selecciona tu rol (opcional)</option>
              <option>Lo estoy viviendo</option>
              <option>Soy testigo</option>
              <option>Soy familiar</option>
              <option>Soy docente</option>
              <option>Otro</option>
            </select>
            <p className="text-xs text-center" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Si no quieres elegir, no pasa nada.
            </p>
            <button 
              type="submit"
              disabled={enviando}
              className="transition-all hover:brightness-110"
              style={{ 
                padding: '14px 32px', 
                borderRadius: '9999px', 
                background: enviando ? '#A78BFA' : '#7B4EFF', 
                color: 'white', 
                fontSize: '16px', 
                fontWeight: 600, 
                border: 'none', 
                cursor: enviando ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'Poppins, system-ui, sans-serif',
                boxShadow: '0 2px 8px rgba(123, 78, 255, 0.2)',
                opacity: enviando ? 0.7 : 1
              }}
            >
              {enviando ? 'Enviando...' : 'Enviar historia'}
            </button>
          </div>

          {mensaje && (
            <p className="text-center mt-4 text-base font-semibold" style={{ 
              color: mensaje.includes('✓') ? '#00C3A8' : '#FF5F91',
              fontFamily: 'Inter, system-ui, sans-serif'
            }}>
              {mensaje}
            </p>
          )}

          {/* Texto de salida segura */}
          <p className="text-sm text-center mt-4" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic' }}>
            Puedes parar o salir cuando quieras. No pasa nada si no envías nada.
          </p>
        </form>

        {/* Normas de cuidado - bloque informativo */}
        <div className="mt-8 rounded-2xl p-6 sm:p-8" style={{ background: 'rgba(100, 116, 139, 0.04)', border: '1px solid rgba(100, 116, 139, 0.08)' }}>
          <p className="font-semibold mb-4" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.9rem' }}>
            🛡️ Normas de cuidado
          </p>
          <ul className="space-y-2 mb-5" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.85rem' }}>
            <li>✓ No compartas nombres reales ni lugares específicos.</li>
            <li>✓ Piensa que quien lo lea también puede estar pasando por algo parecido.</li>
            <li>✓ Este espacio es para compartir, sin hacer daño.</li>
            <li>✓ Si hay peligro inmediato, llama al 112 o busca a una persona de confianza.</li>
          </ul>
          <p className="mb-3" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.85rem' }}>
            📝 Todas las historias son revisadas antes de publicarse.<br/>
            Si aparece algún dato personal, puede que no se publiquen o que se cambien para cuidar la privacidad.
          </p>
          <p style={{ color: '#A1A1AA', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.8rem' }}>
            Este espacio no sustituye apoyo profesional ni servicios de emergencia.
          </p>
        </div>
      </div>
    </BaseLayout>
  );
};

const Aprende = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  return (
  <BaseLayout>
    {/* Bloque inicial Aprende */}
    <div className="rounded-3xl p-8 sm:p-12 lg:p-16 mb-10 text-center" style={{ background: 'white', boxShadow: '0 2px 12px rgba(123, 78, 255, 0.08)' }}>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', lineHeight: 1.1 }}>
        Aprende
      </h1>
      <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
        Pasos concretos para saber qué decir, qué hacer y cómo ayudar
      </p>
    </div>

    <div className="max-w-4xl mx-auto">
      
      {/* ========== SEÑALES QUE NO CONVIENE IGNORAR ========== */}
      <div className="mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Señales que no conviene ignorar
        </h2>

        <div className="space-y-4">
          {/* Acordeón: Si te pasa a ti */}
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '3px solid #FFB4A2' }}>
            <button 
              onClick={() => toggleAccordion('si-te-pasa')}
              className="w-full p-5 text-left flex justify-between items-center"
              style={{ background: openAccordion === 'si-te-pasa' ? '#F8FAFC' : 'white' }}
            >
              <span className="text-lg font-semibold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Si te pasa a ti
              </span>
              <span style={{ color: '#64748B', fontSize: '20px', transform: openAccordion === 'si-te-pasa' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                ▼
              </span>
            </button>
            {openAccordion === 'si-te-pasa' && (
              <div className="px-5 pb-5">
                <p className="text-base mb-4 p-4 rounded-xl" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', background: 'rgba(123, 78, 255, 0.04)', fontStyle: 'italic' }}>
                  Cuando algo se repite y te afecta, no es normal.<br/>
                  Lo que te hace sentir mal importa.
                </p>
                <ul className="text-base space-y-2 mb-5" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <li>• Te cuesta ir al centro o buscas excusas para no ir.</li>
                  <li>• Vuelves con un nudo en el estómago o con miedo sin saber explicarlo.</li>
                  <li>• Te aíslas más de lo habitual o prefieres estar solo/a.</li>
                  <li>• Has dejado cosas que antes te gustaban.</li>
                  <li>• Te sientes culpable, te hacen creer que el problema eres tú.</li>
                  <li>• Evitas contarlo porque crees que no te van a creer.</li>
                  <li>• Cuando lo cuentas, te escuchan… pero nada cambia.</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a href="#pasos" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(123, 78, 255, 0.1)', color: '#7B4EFF', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Leer qué puedes hacer
                  </a>
                  <a href="#recursos-ayuda" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(0, 195, 168, 0.1)', color: '#00C3A8', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Buscar apoyo
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Acordeón: Si lo ves */}
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '3px solid #FFB4A2' }}>
            <button 
              onClick={() => toggleAccordion('si-lo-ves')}
              className="w-full p-5 text-left flex justify-between items-center"
              style={{ background: openAccordion === 'si-lo-ves' ? '#F8FAFC' : 'white' }}
            >
              <span className="text-lg font-semibold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Si lo ves
              </span>
              <span style={{ color: '#64748B', fontSize: '20px', transform: openAccordion === 'si-lo-ves' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                ▼
              </span>
            </button>
            {openAccordion === 'si-lo-ves' && (
              <div className="px-5 pb-5">
                <p className="text-base mb-4 p-4 rounded-xl" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', background: 'rgba(123, 78, 255, 0.04)', fontStyle: 'italic' }}>
                  Cuando algo se repite y te incomoda, no es normal.<br/>
                  Lo que te hace sentir mal importa.
                </p>
                <ul className="text-base space-y-2 mb-5" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <li>• Siempre se meten con la misma persona.</li>
                  <li>• Hay motes, burlas o risas que no hacen gracia.</li>
                  <li>• Alguien acaba quedándose al margen una y otra vez.</li>
                  <li>• Circulan rumores o mensajes que humillan.</li>
                  <li>• Ves empujones, insultos o gestos que hacen daño.</li>
                  <li>• Notas que alguien tiene miedo, se esconde o evita al grupo.</li>
                  <li>• Te incomoda lo que pasa, pero no sabes qué hacer.</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a href="#pasos" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(123, 78, 255, 0.1)', color: '#7B4EFF', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Leer qué puedes hacer
                  </a>
                  <a href="#recursos-ayuda" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(0, 195, 168, 0.1)', color: '#00C3A8', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Buscar apoyo
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Acordeón: Si te preocupa alguien */}
          <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '3px solid #FFB4A2' }}>
            <button 
              onClick={() => toggleAccordion('si-te-preocupa')}
              className="w-full p-5 text-left flex justify-between items-center"
              style={{ background: openAccordion === 'si-te-preocupa' ? '#F8FAFC' : 'white' }}
            >
              <span className="text-lg font-semibold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Si te preocupa alguien
              </span>
              <span style={{ color: '#64748B', fontSize: '20px', transform: openAccordion === 'si-te-preocupa' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                ▼
              </span>
            </button>
            {openAccordion === 'si-te-preocupa' && (
              <div className="px-5 pb-5">
                <p className="text-base mb-4 p-4 rounded-xl" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', background: 'rgba(123, 78, 255, 0.04)', fontStyle: 'italic' }}>
                  Cuando algo se repite y te inquieta, no es normal.<br/>
                  Lo que te hace sentir mal importa.
                </p>
                <ul className="text-base space-y-2 mb-5" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <li>• Hay comportamientos que tratan mal a otras personas de forma habitual.</li>
                  <li>• Aparecen burlas, humillaciones o formas de imponerse usando el miedo.</li>
                  <li>• Falta empatía o cuesta ponerse en el lugar de los demás.</li>
                  <li>• Se justifican estas conductas diciendo que "son bromas" o que "se lo buscan".</li>
                  <li>• Se repiten reacciones de ira o desprecio difíciles de controlar.</li>
                  <li>• Parece que dominar o controlar da sensación de poder.</li>
                  <li>• Algo de lo que ves te inquieta, aunque no sepas explicarlo del todo.</li>
                </ul>
                <div className="flex flex-wrap gap-3">
                  <a href="#pasos" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(123, 78, 255, 0.1)', color: '#7B4EFF', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Leer qué puedes hacer
                  </a>
                  <a href="#recursos-ayuda" className="inline-block px-5 py-2 rounded-full text-sm font-medium" style={{ background: 'rgba(0, 195, 168, 0.1)', color: '#00C3A8', textDecoration: 'none', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    Buscar apoyo
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========== TARJETAS DE CONTEXTO ========== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {/* Tarjeta 1: Cuando una broma deja de serlo */}
        <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
            Cuando una broma deja de serlo
          </h3>
          <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.7 }}>
            Las burlas repetidas desgastan, aíslan y hacen sentir a alguien que el problema es suyo.
          </p>
        </div>

        {/* Tarjeta 2: Por qué a veces nadie hace nada */}
        <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
            Por qué a veces nadie hace nada
          </h3>
          <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.7 }}>
            El miedo, la duda o no saber cómo actuar hacen que muchas personas se queden paralizadas. No significa que no les importe.
          </p>
        </div>
      </div>

      {/* ========== BLOQUE DE ACOMPAÑAMIENTO ========== */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 mb-10" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Cosas que ayudan (y cosas que no)
        </h3>
        <p className="text-base mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.7 }}>
          Se quedan a tu lado, sin forzarte a decidir nada. Solo estando.
        </p>
        <p className="text-sm" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif' }}>
          No presionar ni exigir decisiones.
        </p>
      </div>

      {/* ========== BLOQUE DE EMERGENCIA ========== */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 mb-10" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
        <h3 className="text-xl font-bold mb-4" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Si hay peligro inmediato
        </h3>
        <p className="text-base mb-3" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Llama al <strong>112</strong> o busca a una persona de confianza.
        </p>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Línea de atención al menor: 116 111<br/>
          24h, gratuita y confidencial
        </p>
      </div>

      {/* ========== PERSONAS Y ESPACIOS CERCANOS ========== */}
      <div className="mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Personas y espacios cercanos
        </h2>
        <p className="text-base mb-6 text-center" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          A veces, hablar con alguien cercano es el primer paso.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <h4 className="text-base font-semibold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Tutoría u orientación del centro educativo
            </h4>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Puedes hablar en privado.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <h4 className="text-base font-semibold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Educador o educadora social (Ayuntamiento)
            </h4>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Escucha y ayuda sin exponerte.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <h4 className="text-base font-semibold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Centros juveniles / Gaztelekus
            </h4>
            <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Espacios con personas acostumbradas a acompañar.
            </p>
          </div>
        </div>
      </div>

      {/* ========== PASOS ========== */}
      <div id="pasos" className="mb-10 scroll-mt-24">
        <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Pasos
        </h2>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#7B4EFF', color: 'white', fontWeight: 700, fontFamily: 'Poppins, system-ui, sans-serif' }}>
                1
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                  👀 Reconoce las señales
                </h3>
                <ul className="text-base space-y-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <li>• Cambios de humor repentinos</li>
                  <li>• No querer ir al colegio</li>
                  <li>• Aislamiento social</li>
                  <li>• Marcas físicas sin explicación</li>
                  <li>• Pérdida de objetos personales</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#00C3A8', color: 'white', fontWeight: 700, fontFamily: 'Poppins, system-ui, sans-serif' }}>
                2
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                  💬 Habla con respeto y empatía
                </h3>
                <p className="text-base mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <strong>Di:</strong> &ldquo;He notado que estás diferente. ¿Quieres hablar?&rdquo;
                </p>
                <p className="text-base" style={{ color: '#FF5F91', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <strong>No digas:</strong> &ldquo;¿Por qué no te defiendes?&rdquo; o &ldquo;Seguro exageras&rdquo;
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#FF5F91', color: 'white', fontWeight: 700, fontFamily: 'Poppins, system-ui, sans-serif' }}>
                3
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                  👂 Escucha sin juzgar
                </h3>
                <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Deja que la persona hable a su ritmo. No interrumpas ni minimices lo que siente. Tu presencia ya ayuda.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#7B4EFF', color: 'white', fontWeight: 700, fontFamily: 'Poppins, system-ui, sans-serif' }}>
                4
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                  📝 Documenta los hechos
                </h3>
                <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Guarda capturas, mensajes y fechas.<br/>
                  Puede ser útil si más adelante se necesita ayuda del centro educativo u otros apoyos.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#00C3A8', color: 'white', fontWeight: 700, fontFamily: 'Poppins, system-ui, sans-serif' }}>
                5
              </div>
              <div>
                <h3 className="text-xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                  🧭 Busca apoyo
                </h3>
                <p className="text-base mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Habla con alguien de confianza hasta que te crean:<br/>
                  en el centro educativo o en tu entorno cercano.
                </p>
                <p className="text-base mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  No tienes que hacerlo solo.
                </p>
                <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  <a 
                    href="#recursos-ayuda" 
                    style={{ 
                      color: '#00C3A8', 
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px'
                    }}
                  >
                    Consulta recursos de ayuda
                  </a>
                  {' '}si necesitas contactos o líneas de atención.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========== SECCIÓN RECURSOS DE AYUDA ========== */}
      <div id="recursos-ayuda" className="max-w-4xl mx-auto mt-16 scroll-mt-24">
        <div className="rounded-3xl p-8 sm:p-10 text-center mb-8" style={{ background: 'linear-gradient(135deg, #F0FDF9 0%, #E0F2FE 100%)', border: '1px solid rgba(0, 195, 168, 0.2)' }}>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
            Recursos de ayuda
          </h2>
          <p className="text-base sm:text-lg" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
            Recibir ayuda es posible.<br/>
            Hay personas preparadas para escucharte y ayudarte.
          </p>
        </div>

        {/* Líneas telefónicas */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 mb-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
          <div className="flex items-center gap-3 mb-5">
            <span style={{ fontSize: '24px' }}>📞</span>
            <h3 className="text-xl font-bold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Líneas de atención telefónica
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl" style={{ background: '#F8FAFC' }}>
              <div>
                <p className="font-semibold text-base" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Teléfono ANAR</p>
                <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Ayuda a niños y adolescentes en riesgo</p>
              </div>
              <a href="tel:900202010" className="mt-2 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold" style={{ background: '#00C3A8', color: 'white', fontFamily: 'Poppins, system-ui, sans-serif', textDecoration: 'none', fontSize: '15px' }}>
                900 20 20 10
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl" style={{ background: '#F8FAFC' }}>
              <div>
                <p className="font-semibold text-base" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Teléfono de la Infancia</p>
                <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Línea de atención al menor (24h)</p>
              </div>
              <a href="tel:116111" className="mt-2 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold" style={{ background: '#00C3A8', color: 'white', fontFamily: 'Poppins, system-ui, sans-serif', textDecoration: 'none', fontSize: '15px' }}>
                116 111
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl" style={{ background: '#F8FAFC' }}>
              <div>
                <p className="font-semibold text-base" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Teléfono de la Esperanza</p>
                <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Apoyo emocional 24h</p>
              </div>
              <a href="tel:717003717" className="mt-2 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold" style={{ background: '#00C3A8', color: 'white', fontFamily: 'Poppins, system-ui, sans-serif', textDecoration: 'none', fontSize: '15px' }}>
                717 003 717
              </a>
            </div>
          </div>
          <p className="text-xs mt-4 text-center" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic' }}>
            Todas las líneas son gratuitas y confidenciales
          </p>
        </div>

        {/* Recursos online */}
        <div className="bg-white rounded-2xl p-6 sm:p-8" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
          <div className="flex items-center gap-3 mb-5">
            <span style={{ fontSize: '24px' }}>🌐</span>
            <h3 className="text-xl font-bold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Recursos online
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a 
              href="https://www.anar.org" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-4 rounded-xl transition-all hover:shadow-md"
              style={{ background: '#F8FAFC', textDecoration: 'none', border: '1px solid transparent' }}
            >
              <p className="font-semibold text-base mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Fundación ANAR</p>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Chat y recursos para menores</p>
            </a>
            <a 
              href="https://www.save-the-children.es" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-4 rounded-xl transition-all hover:shadow-md"
              style={{ background: '#F8FAFC', textDecoration: 'none', border: '1px solid transparent' }}
            >
              <p className="font-semibold text-base mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Save the Children</p>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Información sobre acoso escolar</p>
            </a>
            <a 
              href="https://www.incibe.es/menores" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block p-4 rounded-xl transition-all hover:shadow-md"
              style={{ background: '#F8FAFC', textDecoration: 'none', border: '1px solid transparent' }}
            >
              <p className="font-semibold text-base mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>INCIBE - Internet Segura</p>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Ciberbullying y seguridad digital</p>
            </a>
            <div className="block p-4 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid transparent' }}>
              <p className="font-semibold text-base mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Protección del menor – unidad especializada</p>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Actúan cuando se necesita protección legal.</p>
            </div>
            <div className="block p-4 rounded-xl" style={{ background: '#F8FAFC', border: '1px solid transparent' }}>
              <p className="font-semibold text-base mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>Defensor del Menor</p>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>Protege los derechos de niños y adolescentes.</p>
            </div>
          </div>
        </div>

        {/* Mensaje final de apoyo */}
        <div className="mt-10 text-center p-6 rounded-2xl" style={{ background: 'rgba(123, 78, 255, 0.05)', border: '1px solid rgba(123, 78, 255, 0.1)' }}>
          <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.8 }}>
            Pedir ayuda es un paso importante para que las cosas cambien.<br/>
            <span style={{ color: '#7B4EFF' }}>Mereces sentirte bien.</span>
          </p>
        </div>
      </div>
    </div>
  </BaseLayout>
  );
};

const Muro = () => {
  const { historias, loading } = useHistorias();

  // Filtrar solo historias aprobadas y ordenar por más reciente primero
  const historiasAprobadas = historias
    .filter(h => h.status === 'aprobada')
    .sort((a, b) => b.timestamp - a.timestamp);

  return (
    <BaseLayout>
      <div className="rounded-3xl p-8 sm:p-12 lg:p-16 mb-10 text-center" style={{ background: 'white', boxShadow: '0 2px 12px rgba(123, 78, 255, 0.08)' }}>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', lineHeight: 1.1 }}>
          Historias
        </h1>
        <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Lee historias reales de gente como tú.<br/>
          Otras personas también han vivido algo parecido.
        </p>
      </div>

      {/* Listado de historias aprobadas */}
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          📖 Historias publicadas
        </h2>

        {loading ? (
          <div className="bg-white rounded-2xl p-8 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Cargando historias...
            </p>
          </div>
        ) : historiasAprobadas.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Aún no hay historias publicadas. Sé el primero en compartir.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {historiasAprobadas.map((historia) => (
              <div 
                key={historia.id} 
                className="bg-white rounded-2xl p-6" 
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: `4px solid ${historia.color}` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center" 
                    style={{ background: historia.bgColor }}
                  >
                    <span style={{ fontSize: '20px' }}>{historia.emoji}</span>
                  </div>
                  <div>
                    <p className="font-bold text-base" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                      {historia.tipo}
                    </p>
                    <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {historia.fecha}
                    </p>
                  </div>
                </div>
                {historia.titulo && (
                  <h3 className="text-lg font-bold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                    {historia.titulo}
                  </h3>
                )}
                <p className="text-base leading-relaxed" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'pre-line' }}>
                  {historia.contenido}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <p className="text-base italic" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
            Estas son historias reales compartidas de forma anónima.
          </p>
        </div>
      </div>
    </BaseLayout>
  );
};

const Comics = () => (
  <BaseLayout>
    <div className="rounded-2xl p-6 sm:p-8 lg:p-12 mb-8 text-center" style={{ background: '#FFF8ED', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4" style={{ color: 'rgb(30, 41, 59)' }}>
        Cómics
      </h1>
      <p className="text-sm sm:text-base md:text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: 'rgb(71, 85, 105)' }}>
        Historias visuales que ayudan a entender y sentir.
      </p>
    </div>

    <div className="max-w-[900px] mx-auto space-y-12">
      {/* Cómic principal (nuevo - 6 viñetas) */}
      <div className="bg-white rounded-lg p-6 sm:p-8" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <h2 className="text-xl font-semibold mb-4 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Una historia
        </h2>
        <img 
          src="https://customer-assets.emergentagent.com/job_anti-bullying-4/artifacts/t47objjw_foto%202%20comics%20proyecto%20ecos.png"
          alt="Cómic - Una historia"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '12px',
            display: 'block'
          }}
        />
        <p className="text-center mt-6 text-lg" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic' }}>
          A veces, basta con que alguien se quede.
        </p>
      </div>

      {/* Cómic anterior (corregido) */}
      <div className="bg-white rounded-lg p-6 sm:p-8" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <img 
          src="https://customer-assets.emergentagent.com/job_anti-bullying-4/artifacts/ppl29qbz_Comic%20proyecto%20ecos.png"
          alt="Cómic - Ecos del Silencio"
          style={{
            width: '100%',
            height: 'auto',
            borderRadius: '12px',
            display: 'block'
          }}
        />
      </div>
    </div>
  </BaseLayout>
);

// Página de Política de Privacidad
const Privacidad = () => (
  <BaseLayout>
    <div className="max-w-3xl mx-auto">
      <div className="rounded-2xl p-6 sm:p-8 lg:p-12 mb-8" style={{ background: 'white', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)' }}>
        <h1 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Política de privacidad
        </h1>
        
        <div className="space-y-6" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.95rem', lineHeight: 1.7 }}>
          <p>
            Ecos del Silencio es un espacio digital pensado para compartir experiencias relacionadas con bullying, exclusión o presión social.
          </p>
          <p>
            La privacidad y la seguridad de las personas que visitan este sitio son una prioridad.
          </p>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Datos personales
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Este sitio no solicita nombre, correo electrónico ni datos personales para navegar o leer contenidos.</li>
              <li>Cuando una persona decide compartir una experiencia, el texto se envía de forma anónima.</li>
              <li>Se recomienda no incluir nombres reales, centros educativos ni lugares específicos dentro de las historias.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Moderación de contenidos
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Las historias enviadas son revisadas antes de publicarse.</li>
              <li>Durante esa revisión se pueden eliminar o modificar fragmentos que contengan datos personales, referencias que permitan identificar a alguien o información sensible.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Uso de la información
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Las historias publicadas se utilizan únicamente para compartir experiencias y ayudar a otras personas a comprender situaciones similares.</li>
              <li>No se utilizan con fines comerciales ni publicitarios.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Cookies
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Este sitio puede utilizar cookies técnicas necesarias para el funcionamiento de la página.</li>
              <li>No se utilizan cookies de seguimiento ni publicidad.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Servicios externos
            </h2>
            <p>
              En la sección de recursos pueden aparecer enlaces a organizaciones externas. Cada uno de esos sitios tiene su propia política de privacidad.
            </p>
          </div>
        </div>
      </div>
    </div>
  </BaseLayout>
);

// Página de Aviso Legal
const AvisoLegal = () => (
  <BaseLayout>
    <div className="max-w-3xl mx-auto">
      <div className="rounded-2xl p-6 sm:p-8 lg:p-12 mb-8" style={{ background: 'white', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)' }}>
        <h1 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          Aviso legal
        </h1>
        
        <div className="space-y-6" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.95rem', lineHeight: 1.7 }}>
          <p>
            Ecos del Silencio es un espacio digital de información y acompañamiento.
          </p>
          <p>
            El contenido publicado en esta web tiene fines informativos, educativos y de apoyo.
          </p>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Responsabilidad sobre contenidos
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>Las historias publicadas son experiencias compartidas por personas de forma anónima.</li>
              <li>Aunque se revisan antes de publicarse, reflejan vivencias personales y no constituyen asesoramiento profesional.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Recursos de ayuda
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>La web incluye información sobre recursos externos como líneas de atención o entidades especializadas.</li>
              <li>Ecos del Silencio no sustituye a profesionales ni servicios de emergencia.</li>
              <li>En situaciones de riesgo o emergencia se recomienda contactar con servicios especializados o con el 112.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
              Uso del sitio
            </h2>
            <p>
              El uso de este sitio implica aceptar que se trata de un espacio de lectura y reflexión. Los contenidos no pueden utilizarse para identificar o atacar a terceras personas.
            </p>
          </div>
        </div>
      </div>
    </div>
  </BaseLayout>
);

const Compromisos = () => {
  const [selectedOption, setSelectedOption] = useState('');
  const [customText, setCustomText] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);

  // Cargar contador total desde Supabase al montar
  useEffect(() => {
    const cargarContador = async () => {
      try {
        const { count, error } = await supabase
          .from('ecos_commitments')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        setTotalCount(count || 0);
      } catch (error) {
        console.error('Error al cargar contador:', error);
      } finally {
        setLoadingCount(false);
      }
    };
    
    cargarContador();
  }, []);

  const handleSave = async () => {
    // Validación: no permitir guardar si no hay opción seleccionada o texto vacío en compromiso propio
    if (!selectedOption || selectedOption === 'Selecciona un compromiso') {
      return;
    }
    
    const isCustom = selectedOption.includes('propio');
    if (isCustom && !customText.trim()) {
      return;
    }

    setEnviando(true);

    try {
      const commitmentText = isCustom ? customText.trim() : selectedOption;
      
      const { error } = await supabase
        .from('ecos_commitments')
        .insert([{ text: commitmentText, is_public: true }]);
      
      if (error) throw error;
      
      // Actualizar contador local
      setTotalCount(prev => prev + 1);

      // Mostrar confirmación
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);

      // Limpiar formulario
      setSelectedOption('');
      setCustomText('');

      // Log para debugging
      console.log('commitment_saved');
    } catch (error) {
      console.error('Error al guardar compromiso:', error);
    } finally {
      setEnviando(false);
    }
  };

  return (
  <BaseLayout>
    <div className="rounded-3xl p-8 sm:p-12 lg:p-16 mb-10 text-center" style={{ background: 'white', boxShadow: '0 2px 12px rgba(123, 78, 255, 0.08)' }}>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-5" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', lineHeight: 1.1 }}>
        Compromisos
      </h1>
      <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-3xl mx-auto" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
        Da un gran paso. Pequeñas acciones, grandes cambios.
      </p>
    </div>

    {/* Crear nuevo compromiso */}
    <div className="max-w-4xl mx-auto mb-12">
      <div className="bg-white rounded-2xl p-6 sm:p-8 mb-8" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '4px solid #00C3A8' }}>
        <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          💪 Elige tu compromiso
        </h2>
        <p className="text-base mb-6 text-center" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Elige una acción concreta que puedes hacer para ayudar a crear ambientes más seguros.
        </p>
        
        <label className="block text-sm font-medium mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Selecciona un compromiso
        </label>
        <select 
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          className="w-full p-4 border rounded-xl text-base mb-4" 
          style={{ borderColor: '#E2E8F0', fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <option value="">Selecciona un compromiso</option>
          <option value="Me comprometo a no quedarme callado/a si veo bullying">Me comprometo a no quedarme callado/a si veo bullying</option>
          <option value="Me comprometo a acompañar a quien lo necesite">Me comprometo a acompañar a quien lo necesite</option>
          <option value="Me comprometo a tratar a todos con respeto">Me comprometo a tratar a todos con respeto</option>
          <option value="Me comprometo a pedir ayuda si lo necesito">Me comprometo a pedir ayuda si lo necesito</option>
          <option value="Me comprometo a escuchar sin juzgar">Me comprometo a escuchar sin juzgar</option>
          <option value="Crear mi propio compromiso...">Crear mi propio compromiso...</option>
        </select>
        
        <textarea 
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder="Escribe tu compromiso personalizado (opcional)..."
          rows="3"
          className="w-full p-4 border rounded-xl text-base mb-4"
          style={{ borderColor: '#E2E8F0', color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}
        ></textarea>
        
        {showConfirmation && (
          <div className="mb-4 p-3 rounded-xl text-center" style={{ background: '#E6FAF5', color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500 }}>
            Compromiso guardado ✅
          </div>
        )}
        
        <button 
          onClick={handleSave}
          disabled={enviando}
          className="transition-all hover:brightness-110"
          style={{ 
            width: '100%',
            padding: '16px 32px', 
            borderRadius: '9999px', 
            background: enviando ? '#A78BFA' : '#7B4EFF', 
            color: 'white', 
            fontSize: '17px', 
            fontWeight: 600, 
            border: 'none', 
            cursor: enviando ? 'not-allowed' : 'pointer',
            fontFamily: 'Poppins, system-ui, sans-serif',
            boxShadow: '0 4px 16px rgba(123, 78, 255, 0.25)',
            opacity: enviando ? 0.7 : 1
          }}
        >
          {enviando ? 'Guardando...' : 'Guardar compromiso'}
        </button>
      </div>
    </div>

    {/* Compromisos activos */}
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
        🌟 Compromisos registrados
      </h2>

      <div className="bg-white rounded-2xl p-8 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderTop: '4px solid #7B4EFF' }}>
        <div className="text-6xl mb-4">🙌</div>
        <div className="text-5xl font-bold mb-2" style={{ color: '#7B4EFF', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          {loadingCount ? '...' : totalCount.toLocaleString('es-ES')}
        </div>
        <p className="text-xl font-semibold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
          compromisos registrados
        </p>
        <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Personas comprometidas a hacer la diferencia
        </p>
      </div>

      <div className="text-center mt-10">
        <p className="text-base italic" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
          Cada compromiso cuenta.<br/>
          El cambio se construye paso a paso.
        </p>
      </div>
    </div>
  </BaseLayout>
  );
};

// ========== COMPONENTE PRESENTACIÓN (SCROLL ANIMADO) ==========
const Presentacion = () => {
  const [visibleSections, setVisibleSections] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    const sections = document.querySelectorAll('.presentacion-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const sectionStyle = (id) => ({
    opacity: visibleSections[id] ? 1 : 0,
    transform: visibleSections[id] ? 'translateY(0)' : 'translateY(40px)',
    transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
  });

  return (
    <div style={{ background: '#FAFCFB', minHeight: '100vh' }}>
      {/* Hero de presentación */}
      <div 
        style={{ 
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '80px 24px',
          background: 'linear-gradient(180deg, #E7FFF8 0%, #FAFCFB 100%)'
        }}
      >
        <h1 
          className="animate-fade-in-up"
          style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 700,
            color: '#1E293B',
            fontFamily: 'Poppins, system-ui, sans-serif',
            marginBottom: '24px',
            letterSpacing: '-0.02em'
          }}
        >
          Ecos del Silencio
        </h1>
        <p 
          className="animate-fade-in-up animation-delay-200"
          style={{
            fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
            color: '#64748B',
            fontFamily: 'Inter, system-ui, sans-serif',
            maxWidth: '600px',
            lineHeight: 1.7,
            fontWeight: 400
          }}
        >
          Un espacio que puede ayudar,<br/>
          incluso cuando no sabes cómo empezar.
        </p>
      </div>

      {/* Bloques de contenido */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px 120px' }}>

        {/* ===== ILUSTRACIÓN 1: Habitación con ventana y luz suave ===== */}
        <div 
          id="img-1" 
          className="presentacion-section"
          style={{ ...sectionStyle('img-1'), padding: '40px 0 60px', display: 'flex', justifyContent: 'center' }}
        >
          <div 
            style={{
              width: '100%',
              maxWidth: '380px',
              aspectRatio: '5/4',
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d66a33d5-b029-47ee-a186-8da7f8c2b867/artifacts/23vdpil7_image.png)',
              backgroundSize: '280% 320%',
              backgroundPosition: '2% 2%'
            }}
            aria-label="Habitación acogedora con ventana y luz suave"
          />
        </div>
        
        {/* Bloque 1 */}
        <div 
          id="section-1" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-1'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <p style={{ fontSize: '1.25rem', color: '#475569', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9 }}>
            Puede que hayas llegado aquí buscando información,<br/>
            <span style={{ color: '#64748B' }}>como madre, padre, familiar, docente…</span><br/>
            <span style={{ color: '#64748B' }}>o simplemente como alguien que quiere entender mejor.</span>
          </p>
          <p style={{ fontSize: '1.15rem', color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '32px', fontWeight: 500 }}>
            No hace falta tener certezas para estar aquí.
          </p>
        </div>

        {/* Bloque 2 */}
        <div 
          id="section-2" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-2'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            No siempre es fácil saber qué le pasa a una persona joven.
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9 }}>
            A veces no hablan porque no saben explicarlo,<br/>
            no quieren preocuparse<br/>
            o no saben si "es para tanto".
          </p>
        </div>

        {/* ===== ILUSTRACIÓN 2: Mochila y habitación ===== */}
        <div 
          id="img-2" 
          className="presentacion-section"
          style={{ ...sectionStyle('img-2'), padding: '40px 0', display: 'flex', justifyContent: 'center' }}
        >
          <div 
            style={{
              width: '100%',
              maxWidth: '380px',
              aspectRatio: '5/4',
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d66a33d5-b029-47ee-a186-8da7f8c2b867/artifacts/23vdpil7_image.png)',
              backgroundSize: '280% 320%',
              backgroundPosition: '98% 2%'
            }}
            aria-label="Habitación con mochila escolar"
          />
        </div>

        {/* Bloque 3 */}
        <div 
          id="section-3" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-3'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            El silencio no siempre significa que todo esté bien.
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9 }}>
            Muchas veces significa que<br/>
            <strong style={{ color: '#475569' }}>no hay un lugar seguro para expresarse.</strong>
          </p>
        </div>

        {/* ===== ILUSTRACIÓN 3: Puerta abierta con luz ===== */}
        <div 
          id="img-3" 
          className="presentacion-section"
          style={{ ...sectionStyle('img-3'), padding: '40px 0', display: 'flex', justifyContent: 'center' }}
        >
          <div 
            style={{
              width: '100%',
              maxWidth: '340px',
              aspectRatio: '1/1',
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d66a33d5-b029-47ee-a186-8da7f8c2b867/artifacts/23vdpil7_image.png)',
              backgroundSize: '320% 380%',
              backgroundPosition: '0% 70%'
            }}
            aria-label="Puerta abierta con luz"
          />
        </div>

        {/* Bloque 4 */}
        <div 
          id="section-4" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-4'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            Ecos del Silencio no sustituye a nadie.
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9, marginBottom: '24px' }}>
            No ocupa tu lugar.<br/>
            No da lecciones.<br/>
            No señala errores.
          </p>
          <p style={{ fontSize: '1.2rem', color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600 }}>
            Es un apoyo, no un juicio.
          </p>
        </div>

        {/* ===== ILUSTRACIÓN 4: Manos que se apoyan ===== */}
        <div 
          id="img-4" 
          className="presentacion-section"
          style={{ ...sectionStyle('img-4'), padding: '40px 0', display: 'flex', justifyContent: 'center' }}
        >
          <div 
            style={{
              width: '100%',
              maxWidth: '340px',
              aspectRatio: '1/1',
              borderRadius: '16px',
              overflow: 'hidden',
              backgroundImage: 'url(https://customer-assets.emergentagent.com/job_d66a33d5-b029-47ee-a186-8da7f8c2b867/artifacts/23vdpil7_image.png)',
              backgroundSize: '320% 380%',
              backgroundPosition: '100% 70%'
            }}
            aria-label="Manos que se apoyan"
          />
        </div>

        {/* Bloque 5 */}
        <div 
          id="section-5" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-5'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            ¿Qué es Ecos del Silencio?
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9, marginBottom: '24px' }}>
            Un espacio digital donde se comparten experiencias reales,<br/>
            puedes leer sin exponerte<br/>
            y encontrar orientación clara cuando lo necesitas.
          </p>
          <p style={{ fontSize: '1.1rem', color: '#475569', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic' }}>
            Para personas jóvenes y para quienes las acompañan.
          </p>
        </div>

        {/* Bloque 6 */}
        <div 
          id="section-6" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-6'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            Quizá no sea para hablar hoy.
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9, marginBottom: '32px' }}>
            Pero puede ser útil saber que existe.
          </p>
          <p style={{ fontSize: '1.1rem', color: '#475569', fontFamily: 'Inter, system-ui, sans-serif', marginBottom: '16px' }}>
            A veces basta con decir:
          </p>
          <div style={{ 
            background: '#F0FDF9', 
            borderLeft: '4px solid #00C3A8', 
            padding: '24px 28px',
            borderRadius: '0 12px 12px 0'
          }}>
            <p style={{ fontSize: '1.15rem', color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic', lineHeight: 1.8, margin: 0 }}>
              "He visto algo que igual te interesa.<br/>
              Míralo cuando quieras."
            </p>
          </div>
        </div>

        {/* Bloque 7 */}
        <div 
          id="section-7" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-7'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            Aquí no se expone a nadie.
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.9, marginBottom: '24px' }}>
            Sin nombres.<br/>
            Sin juicios.<br/>
            Sin obligación de participar.
          </p>
          <p style={{ fontSize: '1.15rem', color: '#475569', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500 }}>
            Cada persona entra a su ritmo.
          </p>
        </div>

        {/* Bloque 8 */}
        <div 
          id="section-8" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-8'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            ¿Qué puede aportar este espacio?
          </h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {['Sentirse menos aislada.', 'Entender mejor lo que pasa.', 'Saber qué hacer o a quién acudir.'].map((item, i) => (
              <li key={i} style={{ 
                fontSize: '1.15rem', 
                color: '#475569', 
                fontFamily: 'Inter, system-ui, sans-serif',
                padding: '12px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#00C3A8', flexShrink: 0 }}></span>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: '1.1rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '32px', fontStyle: 'italic' }}>
            Nada más. Nada menos.
          </p>
        </div>

        {/* Bloque 9 */}
        <div 
          id="section-9" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-9'), padding: '80px 0', borderBottom: '1px solid #E2E8F0' }}
        >
          <h2 style={{ fontSize: '1.5rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '24px', fontWeight: 600 }}>
            ¿Para quién es Ecos del Silencio?
          </h2>
          <p style={{ fontSize: '1.15rem', color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 2.2 }}>
            Para personas jóvenes.<br/>
            Para familias.<br/>
            Para quienes quieren acompañar mejor.
          </p>
        </div>

        {/* Bloque 10 - Cierre */}
        <div 
          id="section-10" 
          className="presentacion-section"
          style={{ ...sectionStyle('section-10'), padding: '80px 0', textAlign: 'center' }}
        >
          <h2 style={{ fontSize: '1.75rem', color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif', marginBottom: '16px', fontWeight: 600 }}>
            Si estás aquí, ya estás haciendo algo.
          </h2>
          <p style={{ fontSize: '1.25rem', color: '#00C3A8', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, marginBottom: '48px' }}>
            Escuchar también es cuidar.
          </p>

          {/* Botones de acción */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', marginTop: '40px' }}>
            <a 
              href="/muro"
              style={{
                display: 'inline-block',
                padding: '16px 40px',
                background: '#00C3A8',
                color: 'white',
                fontSize: '1rem',
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 600,
                borderRadius: '30px',
                textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              Leer historias
            </a>
            <a 
              href="/aprende"
              style={{
                display: 'inline-block',
                padding: '14px 36px',
                background: 'transparent',
                color: '#475569',
                fontSize: '1rem',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 500,
                borderRadius: '30px',
                textDecoration: 'none',
                border: '2px solid #E2E8F0',
                transition: 'border-color 0.2s'
              }}
            >
              Entender mejor
            </a>
            <a 
              href="/participa"
              style={{
                padding: '14px 36px',
                background: 'transparent',
                color: '#94A3B8',
                fontSize: '0.95rem',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 400,
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '4px'
              }}
            >
              Compartir cuando quieras
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

// Código de acceso para el panel de moderación (fácil de cambiar)
// NOTA: Este código es temporal. Después de crear un usuario admin en Supabase,
// el login usará Supabase Auth para mayor seguridad.

const AdminModeracion = () => {
  const { historias, actualizarStatusHistoria, cargarHistorias, loading } = useHistorias();
  const { user, signIn, signOut } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("todas");
  const [filterCountry, setFilterCountry] = useState("todas");
  const [updatingId, setUpdatingId] = useState(null);
  
  // Estados para edición
  const [editingStory, setEditingStory] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);

  // Cargar todas las historias (incluyendo pendientes) cuando el usuario está autenticado
  useEffect(() => {
    if (user) {
      cargarHistorias(true); // true = modo admin
    }
  }, [user, cargarHistorias]);

  // Abrir modal de edición
  const handleEditClick = (historia) => {
    setEditingStory(historia);
    setEditedContent(historia.contenido);
  };

  // Cerrar modal de edición
  const handleCloseEdit = () => {
    setEditingStory(null);
    setEditedContent("");
  };

  // Guardar cambios de edición
  const handleSaveEdit = async () => {
    if (!editingStory || !editedContent.trim()) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('ecos_stories')
        .update({ content: editedContent.trim() })
        .eq('id', editingStory.id);
      
      if (error) throw error;
      
      // Recargar historias para mostrar el cambio
      await cargarHistorias(true);
      handleCloseEdit();
    } catch (err) {
      console.error('Error al guardar:', err);
      alert('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError("");
    
    try {
      await signIn(email, password);
      // Las historias se cargarán automáticamente por el useEffect
    } catch (err) {
      console.error('Error de login:', err);
      setError(err.message || "Error al iniciar sesión. Verifica tus credenciales.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    const result = await actualizarStatusHistoria(id, newStatus);
    setUpdatingId(null);
    
    if (!result.success) {
      alert('Error al actualizar el estado. Por favor, inténtalo de nuevo.');
    }
  };

  // Filtrado de historias reales
  const filteredHistorias = historias.filter(historia => {
    const matchesSearch = historia.contenido.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "todas" || historia.status === filterStatus;
    const matchesCountry = filterCountry === "todas" || historia.codigo_de_pais === filterCountry;
    return matchesSearch && matchesStatus && matchesCountry;
  });

  // Ordenar: pendientes primero, luego aprobadas, luego el resto
  const sortedHistorias = [...filteredHistorias].sort((a, b) => {
    const statusOrder = { pendiente: 1, aprobada: 2, inapropiada: 3, eliminada: 4 };
    const orderA = statusOrder[a.status] || 5;
    const orderB = statusOrder[b.status] || 5;
    if (orderA !== orderB) return orderA - orderB;
    return b.timestamp - a.timestamp; // Más recientes primero dentro del mismo status
  });

  // Estadísticas de historias reales
  const stats = {
    total: historias.length,
    pendiente: historias.filter(h => h.status === "pendiente").length,
    aprobada: historias.filter(h => h.status === "aprobada").length,
    eliminada: historias.filter(h => h.status === "eliminada").length,
    inapropiada: historias.filter(h => h.status === "inapropiada").length,
  };

  // Pantalla de autenticación con Supabase Auth
  if (!user) {
    return (
      <BaseLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ background: '#E7FFF8' }}>
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'rgba(123, 78, 255, 0.1)' }}>
                <Lock size={32} style={{ color: '#7B4EFF' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Panel de Moderación
              </h2>
              <p className="text-sm" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Inicia sesión con tu cuenta de administrador
              </p>
            </div>

            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="admin@ejemplo.com"
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-purple-500"
                  style={{ 
                    borderColor: error ? '#FF5F91' : '#E2E8F0',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Contraseña
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-purple-500"
                  style={{ 
                    borderColor: error ? '#FF5F91' : '#E2E8F0',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }}
                  required
                />
              </div>
              
              {error && (
                <p className="text-sm mb-4 p-3 rounded-lg" style={{ color: '#FF5F91', background: 'rgba(255, 95, 145, 0.1)', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loginLoading}
                className="w-full py-3 rounded-lg font-semibold transition-all"
                style={{
                  background: loginLoading ? '#A78BFA' : '#7B4EFF',
                  color: 'white',
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  border: 'none',
                  cursor: loginLoading ? 'not-allowed' : 'pointer',
                  opacity: loginLoading ? 0.7 : 1
                }}
              >
                {loginLoading ? 'Iniciando sesión...' : 'Acceder'}
              </button>
            </form>
            
            <p className="text-xs text-center mt-4" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif' }}>
              Acceso restringido a administradores autorizados
            </p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  // Panel de moderación (después de autenticarse)
  return (
    <BaseLayout>
      <div className="min-h-screen" style={{ background: '#E7FFF8' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
          
          {/* Header con botón de logout */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Panel de Moderación
              </h1>
              <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Gestiona y revisa el contenido de la plataforma
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:brightness-95"
              style={{
                background: '#F1F5F9',
                color: '#64748B',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              <LogOut size={18} />
              Cerrar sesión
            </button>
          </div>

          {/* Estadísticas */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                {stats.total}
              </div>
              <div className="text-xs" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Total
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderTop: '3px solid #FFA500' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: '#FFA500', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                {stats.pendiente}
              </div>
              <div className="text-xs" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Pendientes
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderTop: '3px solid #00C3A8' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: '#00C3A8', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                {stats.aprobada}
              </div>
              <div className="text-xs" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Aprobadas
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderTop: '3px solid #64748B' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: '#64748B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                {stats.eliminada}
              </div>
              <div className="text-xs" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Eliminadas
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderTop: '3px solid #FF5F91' }}>
              <div className="text-2xl font-bold mb-1" style={{ color: '#FF5F91', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                {stats.inapropiada}
              </div>
              <div className="text-xs" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Inapropiadas
              </div>
            </div>
          </div>

          {/* Barra de búsqueda y filtros */}
          <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
            {/* Búsqueda */}
            <div className="mb-4">
              <div className="relative">
                <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B' }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por palabras clave..."
                  className="w-full pl-10 pr-4 py-3 rounded-lg border-2 focus:outline-none"
                  style={{ 
                    borderColor: '#E2E8F0',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Filtro de estado */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Filtrar por estado
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:outline-none"
                style={{ 
                  borderColor: '#E2E8F0',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '14px'
                }}
              >
                <option value="todas">Todas</option>
                <option value="pendiente">Pendientes</option>
                <option value="aprobada">Aprobadas</option>
                <option value="eliminada">Eliminadas</option>
                <option value="inapropiada">Inapropiadas</option>
              </select>
            </div>

            {/* Filtro de país */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Filtrar por país
              </label>
              <select
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border-2 focus:outline-none"
                style={{ 
                  borderColor: '#E2E8F0',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '14px'
                }}
              >
                <option value="todas">Todos los países</option>
                <option value="es">🇪🇸 España</option>
                <option value="ar">🇦🇷 Argentina</option>
                <option value="mx">🇲🇽 México</option>
              </select>
            </div>
          </div>

          {/* Lista de historias reales */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-4 mb-4" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)', borderLeft: '4px solid #7B4EFF' }}>
              <p className="text-sm font-semibold" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                📖 Historias reales del sitio ({sortedHistorias.length})
              </p>
            </div>

            {sortedHistorias.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                <p className="text-base" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  No se encontraron historias
                </p>
              </div>
            ) : (
              sortedHistorias.map((historia) => (
                <div key={historia.id} className="bg-white rounded-xl p-6" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Información de la historia */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        {/* Indicador de país */}
                        {historia.codigo_de_pais && (
                          <span 
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{
                              background: '#F1F5F9',
                              color: '#475569',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              border: '1px solid #E2E8F0'
                            }}
                          >
                            {historia.codigo_de_pais === 'es' && '🇪🇸 ES'}
                            {historia.codigo_de_pais === 'ar' && '🇦🇷 AR'}
                            {historia.codigo_de_pais === 'mx' && '🇲🇽 MX'}
                            {!['es', 'ar', 'mx'].includes(historia.codigo_de_pais) && `🌐 ${historia.codigo_de_pais.toUpperCase()}`}
                          </span>
                        )}
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            background: historia.color,
                            color: 'white',
                            fontFamily: 'Poppins, system-ui, sans-serif'
                          }}
                        >
                          {historia.emoji} {historia.tipo}
                        </span>
                        <span 
                          className="px-3 py-1 rounded-full text-xs font-semibold"
                          style={{
                            background: 
                              historia.status === 'pendiente' ? '#FFF3CD' : 
                              historia.status === 'aprobada' ? '#D1FAE5' : 
                              historia.status === 'eliminada' ? '#F1F5F9' : '#FFE4E6',
                            color: 
                              historia.status === 'pendiente' ? '#92400E' : 
                              historia.status === 'aprobada' ? '#065F46' : 
                              historia.status === 'eliminada' ? '#475569' : '#BE123C',
                            fontFamily: 'Poppins, system-ui, sans-serif'
                          }}
                        >
                          {historia.status === 'pendiente' ? '⏳ Pendiente' : 
                           historia.status === 'aprobada' ? '✓ Aprobada' : 
                           historia.status === 'eliminada' ? '🗑️ Eliminada' : '⚠️ Inapropiada'}
                        </span>
                        <span className="text-xs" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          {historia.fecha}
                        </span>
                      </div>
                      {/* Título de la historia (si existe) */}
                      {historia.titulo && (
                        <h4 
                          className="text-base font-semibold mb-2"
                          style={{ 
                            color: '#1E293B', 
                            fontFamily: 'Poppins, system-ui, sans-serif'
                          }}
                        >
                          {historia.titulo}
                        </h4>
                      )}
                      <p className="text-base leading-relaxed" style={{ color: '#1E293B', fontFamily: 'Inter, system-ui, sans-serif', whiteSpace: 'pre-line' }}>
                        {historia.contenido}
                      </p>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex md:flex-col gap-2">
                      <button
                        onClick={() => handleEditClick(historia)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
                        style={{
                          background: '#F1F5F9',
                          color: '#7B4EFF',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontSize: '14px'
                        }}
                      >
                        <Edit size={16} />
                        Editar
                      </button>
                      <button
                        onClick={() => handleStatusChange(historia.id, 'aprobada')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
                        style={{
                          background: historia.status === 'aprobada' ? '#00C3A8' : '#F1F5F9',
                          color: historia.status === 'aprobada' ? 'white' : '#64748B',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontSize: '14px'
                        }}
                        disabled={historia.status === 'aprobada'}
                      >
                        <CheckCircle size={16} />
                        Aprobar
                      </button>
                      <button
                        onClick={() => handleStatusChange(historia.id, 'eliminada')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
                        style={{
                          background: historia.status === 'eliminada' ? '#64748B' : '#F1F5F9',
                          color: historia.status === 'eliminada' ? 'white' : '#64748B',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontSize: '14px'
                        }}
                        disabled={historia.status === 'eliminada'}
                      >
                        <Trash2 size={16} />
                        Eliminar
                      </button>
                      <button
                        onClick={() => handleStatusChange(historia.id, 'inapropiada')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all"
                        style={{
                          background: historia.status === 'inapropiada' ? '#FF5F91' : '#F1F5F9',
                          color: historia.status === 'inapropiada' ? 'white' : '#64748B',
                          border: 'none',
                          cursor: 'pointer',
                          fontFamily: 'Inter, system-ui, sans-serif',
                          fontSize: '14px'
                        }}
                        disabled={historia.status === 'inapropiada'}
                      >
                        <AlertTriangle size={16} />
                        Marcar
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      {editingStory && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={handleCloseEdit}
        >
          <div 
            className="bg-white rounded-2xl p-6 sm:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold" style={{ color: '#1E293B', fontFamily: 'Poppins, system-ui, sans-serif' }}>
                Editar historia
              </h3>
              <button 
                onClick={handleCloseEdit}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
              >
                <X size={24} style={{ color: '#64748B' }} />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span style={{ fontSize: '20px' }}>{editingStory.emoji}</span>
                <span className="font-medium" style={{ color: editingStory.color, fontFamily: 'Inter, system-ui, sans-serif' }}>
                  {editingStory.tipo}
                </span>
                <span className="text-xs px-2 py-1 rounded-full" style={{ 
                  background: editingStory.status === 'pendiente' ? '#FFF3E0' : 
                              editingStory.status === 'aprobada' ? '#E6FAF5' : '#F1F5F9',
                  color: editingStory.status === 'pendiente' ? '#FFA500' : 
                         editingStory.status === 'aprobada' ? '#00C3A8' : '#64748B',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}>
                  {editingStory.status}
                </span>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: '#64748B', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Contenido de la historia
              </label>
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                rows={8}
                className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-purple-500"
                style={{ 
                  borderColor: '#E2E8F0',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '0.95rem',
                  lineHeight: 1.6,
                  resize: 'vertical'
                }}
                placeholder="Escribe el contenido de la historia..."
              />
              <p className="text-xs mt-2" style={{ color: '#94A3B8', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Puedes eliminar nombres, corregir ortografía o quitar detalles identificativos.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseEdit}
                className="px-6 py-3 rounded-lg font-medium transition-all"
                style={{
                  background: '#F1F5F9',
                  color: '#64748B',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editedContent.trim()}
                className="px-6 py-3 rounded-lg font-medium transition-all"
                style={{
                  background: saving ? '#A78BFA' : '#7B4EFF',
                  color: 'white',
                  border: 'none',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  opacity: !editedContent.trim() ? 0.5 : 1
                }}
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </BaseLayout>
  );
};

// ========== ESTRUCTURA PARA VERSIONES POR PAÍS ==========
// Las rutas por país están definidas en la función App()
// Cada país tiene su propio conjunto de rutas con el prefijo correspondiente:
// - /es/* para España
// - /ar/* para Argentina  
// - /mx/* para México
// El CountryContext permite a los componentes acceder a la configuración del país actual

function App() {
  return (
    <AuthProvider>
      <HistoriasProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            <Navbar />
            <Routes>
              {/* ========== RUTAS EXISTENTES (SIN MODIFICAR) ========== */}
              <Route path="/" element={<Home />} />
              <Route path="/presentacion" element={<Presentacion />} />
              <Route path="/participa" element={<Participa />} />
              <Route path="/aprende" element={<Aprende />} />
              <Route path="/muro" element={<Muro />} />
              <Route path="/historias" element={<Muro />} />
              <Route path="/comics" element={<Comics />} />
              <Route path="/compromisos" element={<Compromisos />} />
              <Route path="/privacidad" element={<Privacidad />} />
              <Route path="/aviso-legal" element={<AvisoLegal />} />
              <Route path="/admin/moderacion" element={<AdminModeracion />} />
              
              {/* ========== RUTAS POR PAÍS (Anidadas con Outlet) ========== */}
              {/* España */}
              <Route path="/es" element={<CountryLayout countryCode="es" />}>
                <Route index element={<Home />} />
                <Route path="presentacion" element={<Presentacion />} />
                <Route path="participa" element={<Participa />} />
                <Route path="aprende" element={<Aprende />} />
                <Route path="historias" element={<Muro />} />
                <Route path="comics" element={<Comics />} />
                <Route path="compromisos" element={<Compromisos />} />
                <Route path="privacidad" element={<Privacidad />} />
                <Route path="aviso-legal" element={<AvisoLegal />} />
              </Route>
              
              {/* Argentina */}
              <Route path="/ar" element={<CountryLayout countryCode="ar" />}>
                <Route index element={<Home />} />
                <Route path="presentacion" element={<Presentacion />} />
                <Route path="participa" element={<Participa />} />
                <Route path="aprende" element={<Aprende />} />
                <Route path="historias" element={<Muro />} />
                <Route path="comics" element={<Comics />} />
                <Route path="compromisos" element={<Compromisos />} />
                <Route path="privacidad" element={<Privacidad />} />
                <Route path="aviso-legal" element={<AvisoLegal />} />
              </Route>
              
              {/* México */}
              <Route path="/mx" element={<CountryLayout countryCode="mx" />}>
                <Route index element={<Home />} />
                <Route path="presentacion" element={<Presentacion />} />
                <Route path="participa" element={<Participa />} />
                <Route path="aprende" element={<Aprende />} />
                <Route path="historias" element={<Muro />} />
                <Route path="comics" element={<Comics />} />
                <Route path="compromisos" element={<Compromisos />} />
                <Route path="privacidad" element={<Privacidad />} />
                <Route path="aviso-legal" element={<AvisoLegal />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </div>
      </HistoriasProvider>
    </AuthProvider>
  );
}

export default App;
